// Libraries
import { useState, useEffect } from 'react';

// Components
import Variant from '../../atoms/Variant/Variant';

// Models
import { Pattern } from '../../../features/session/sessionModels';

// Styles
import styles from './Patterns.module.css';

/**
 * @function PatternGrid
 * Describes a single pattern's variants
 *
 * @example
 * return (
 *   <PatternGrid rows={0} columns={0} pattern={} />
 * )
 */
function PatternGrid({
    rows,
    columns,
    pattern,
}: PatternGridProps): JSX.Element {
    const [sequenceIndex, setSequenceIndex] = useState(0);

    useEffect(() => {
        const interval = setInterval(
            () =>
                setSequenceIndex((sequenceIndex) => {
                    return sequenceIndex < pattern.pattern.variants.length - 1
                        ? sequenceIndex + 1
                        : 0;
                }),
            1000,
        );
        return () => clearInterval(interval);
    }, [sequenceIndex]);

    return (
        <Variant
            rows={rows}
            columns={columns}
            sequence={pattern.pattern.variants[sequenceIndex].sequence}
        />
    );
}

/**
 * @function Patterns
 * Describes a list of patterns and their variants
 *
 * @example
 * return (
 *   <Patterns rows={0} columns={0} patterns={[]} />
 * )
 */
function Patterns({ rows, columns, patterns }: PatternsProps): JSX.Element {
    return (
        <figure className={styles.figure}>
            <div
                className={`${styles.grid}${
                    patterns.length > 1 ? ` ${styles.multiple}` : ''
                }`}
            >
                {patterns.map((pattern) => (
                    <PatternGrid
                        key={pattern.id}
                        rows={rows}
                        columns={columns}
                        pattern={pattern}
                    />
                ))}
            </div>
            <figcaption className={styles.caption}>
                {patterns.length === 1
                    ? patterns[0].name
                    : `${patterns.length} Patterns`}
            </figcaption>
        </figure>
    );
}

/**
 * @interface PatternGridProps
 * Describes the properties required for the PatternGrid component
 *
 * @property {array} rows Number of rows in the pattern grid
 * @property {array} columns Number of columns the pattern grid
 * @property {Pattern} pattern The pattern
 */
export interface PatternGridProps {
    rows: number;
    columns: number;
    pattern: Pattern;
}

/**
 * @interface PatternsProps
 * Describes the properties required for the Patterns component
 *
 * @property {array} rows Number of rows in the pattern grid
 * @property {array} columns Number of columns the pattern grid
 * @property {array} patterns List of patterns and their variants
 */
export interface PatternsProps {
    rows: number;
    columns: number;
    patterns: Pattern[];
}

// Exports
export default Patterns;
